import type { Primitive } from '../types/game';

/**
 * Estado mutable de una partida en curso. Es la única fuente de verdad
 * sobre el progreso del jugador y lo que se persiste en localStorage.
 */
export interface GameState {
  /** Identidad de la partida. */
  gameId: string;
  gameVersion: string;
  schemaVersion: string;
  instanceId: string;

  /** Escenario actual. */
  currentScene: string;
  /** Nodo de diálogo activo, si hay una conversación abierta. */
  activeDialogue: string | null;
  activeNode: string | null;

  variables: Record<string, Primitive>;
  /** Conjunto de IDs de items (sin duplicados). */
  items: string[];
  /** Conjunto de IDs de evidencias descubiertas (sin duplicados). */
  evidence: string[];
  /** Conjunto de IDs de objetivos completados (sin duplicados). */
  completedObjectives: string[];
  /** Escenarios visitados (sin duplicados). */
  visitedScenes: string[];
  /** Interacciones de un solo uso ya consumidas. */
  usedInteractions: string[];
  /** Diálogos ya vistos (para diálogos no repetibles). */
  seenDialogues: string[];
  /** Decisiones relevantes registradas por el juego. */
  decisions: string[];
  /** Estado actual de los objetos de escena (ID objeto → ID estado). */
  objectStates: Record<string, string>;
  /** Contenidos educativos ya presentados al jugador (IDs, sin duplicados). */
  presentedContents: string[];
  /** Resultado educativo por ejercicio evaluable (ID de minijuego → registro). */
  exerciseResults: Record<string, ExerciseRecord>;

  /** Argumentación presentada en la conclusión (si procede). */
  conclusion: ConclusionRecord | null;

  /** Desenlace alcanzado; null si la partida sigue en curso. */
  ending: string | null;
  finished: boolean;

  /** Marca de tiempo del último guardado. */
  savedAt: number;
}

export interface ConclusionRecord {
  hypothesis: string;
  evidence: string[];
  measures: string[];
  ending: string;
}

/**
 * Historial educativo de un ejercicio evaluable. Los puntajes están
 * normalizados (0..max, habitualmente max=100) y derivan del resultado del
 * evaluador del minijuego; el marcador usa `best` y nunca suma repetidos.
 */
export interface ExerciseRecord {
  /** Puntaje de la primera sesión registrada. */
  first: number;
  /** Puntaje de la última sesión. */
  last: number;
  /** Mejor puntaje obtenido. */
  best: number;
  /** Número de sesiones registradas (no de clics). */
  attempts: number;
  /** Puntaje máximo posible del ejercicio. */
  max: number;
}

/** Crea un estado inicial vacío (sin aplicar `initialState` del juego). */
export function createEmptyState(ids: {
  gameId: string;
  gameVersion: string;
  schemaVersion: string;
  instanceId: string;
  startScene: string;
}): GameState {
  return {
    gameId: ids.gameId,
    gameVersion: ids.gameVersion,
    schemaVersion: ids.schemaVersion,
    instanceId: ids.instanceId,
    currentScene: ids.startScene,
    activeDialogue: null,
    activeNode: null,
    variables: {},
    items: [],
    evidence: [],
    completedObjectives: [],
    visitedScenes: [],
    usedInteractions: [],
    seenDialogues: [],
    decisions: [],
    objectStates: {},
    presentedContents: [],
    exerciseResults: {},
    conclusion: null,
    ending: null,
    finished: false,
    savedAt: 0
  };
}

/** Copia profunda sencilla y segura del estado (solo datos JSON). */
export function cloneState(state: GameState): GameState {
  return JSON.parse(JSON.stringify(state)) as GameState;
}

/** Inserta un valor en una lista sin duplicados. Devuelve true si se añadió. */
export function addUnique(list: string[], value: string): boolean {
  if (list.includes(value)) return false;
  list.push(value);
  return true;
}
